import { useDispatch, useSelector } from 'react-redux';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { FileUploadButton } from './FileUploadButton';
import { useProfileImageUrl } from '../hooks/useProfileImageUrl';
import { selectCurrentUser, updateProfile } from '../features/auth/authSlice';
import { setProfileImageApi } from '../features/auth/authApi';
import { showToast } from './ui/toast.utils';
import { extractApiMessage } from '../lib/apiError';
import type { Attachment } from '../features/attachments/types';

const PROFILE_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

export function ProfileAvatarUpload() {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const imageUrl = useProfileImageUrl(user?.profileImageAttachmentId);
  const resourceId = user?.id ? String(user.id) : '';

  const handleUploaded = async (attachment: Attachment) => {
    try {
      const updated = await setProfileImageApi(attachment.id);
      dispatch(updateProfile({ profileImageAttachmentId: updated.profileImageAttachmentId ?? null }));
      showToast('Foto de perfil actualizada.', 'success');
    } catch (error) {
      showToast(extractApiMessage(error, 'No se pudo guardar la foto de perfil.'), 'error');
    }
  };

  const initial = user?.email?.charAt(0).toUpperCase() || 'U';

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-full bg-primary shadow-sm">
        {imageUrl ? (
          <img src={imageUrl} alt="Foto de perfil" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-2xl font-semibold text-primary-foreground">
            {initial}
          </div>
        )}
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <PhotoIcon className="h-4 w-4 text-muted-foreground" />
          <span>Foto de perfil</span>
        </div>
        <p className="text-xs text-muted-foreground">
          JPG, PNG o WebP. Máximo 2 MB.
        </p>
        <FileUploadButton
          resourceType="user"
          resourceId={resourceId}
          accept="image/jpeg,image/png,image/webp,image/gif"
          maxBytes={PROFILE_IMAGE_MAX_BYTES}
          label="Cambiar foto"
          showFeedback={false}
          onUploaded={(attachment) => void handleUploaded(attachment)}
        />
      </div>
    </div>
  );
}
